import { T, useGT } from 'gt-react';
import { TextAaIcon } from '@phosphor-icons/react';
import {
  TEXT_COMPONENT_MAX_LENGTH,
  TextComponentSchema,
  type TextComponent,
} from '@erp/shared-types';
import { ChatMarkdown } from '@/components/common/chat/chat-markdown';
import { Textarea } from '@/components/ui/textarea';
import { defineComponentType, type ComponentRegistry } from '../registry';
import { ConfigRow } from './shared';
import { useComponentTextContext, useIsFramed } from '../ComponentFrame';
import { formatReportText } from '../format/reportText';

/**
 * The block's Markdown, its `{placeholders}` filled from the dataset it is
 * bound to; as written when it binds none.
 */
function useShownMarkdown(component: TextComponent): string {
  const context = useComponentTextContext(component);
  const bound = component.datasetKey != null || component.source != null;
  return bound
    ? formatReportText(component.markdown, context)
    : component.markdown;
}

function TextRenderer({ component }: { component: TextComponent }) {
  const markdown = useShownMarkdown(component);
  const framed = useIsFramed();
  if (!component.markdown.trim())
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-muted-foreground">
        <TextAaIcon className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-center text-xs">
          <T context="report text block empty">
            Write this block's text in the Report builder.
          </T>
        </p>
      </div>
    );
  return (
    <div
      className={
        framed ? 'min-w-0' : 'min-h-0 flex-1 overflow-y-auto px-3 pb-2'
      }
    >
      <ChatMarkdown className="[&_h1]:text-lg [&_h1]:font-semibold">
        {markdown}
      </ChatMarkdown>
    </div>
  );
}

function TextConfigForm({
  component,
  onUpdate,
}: {
  component: TextComponent;
  onUpdate: (updated: TextComponent) => void;
}) {
  const gt = useGT();
  return (
    <ConfigRow
      label={gt('Text (Markdown)', { context: 'report text block' })}
      htmlFor={`text-${component.id}`}
    >
      <Textarea
        id={`text-${component.id}`}
        rows={10}
        maxLength={TEXT_COMPONENT_MAX_LENGTH}
        value={component.markdown}
        placeholder={gt('## Summary\n\nNet is up **12%** on last quarter.', {
          context: 'report text block placeholder',
        })}
        onChange={(event) =>
          onUpdate({ ...component, markdown: event.target.value })
        }
      />
    </ConfigRow>
  );
}

function TextPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <rect x="6" y="7" width="22" height="4" rx="1" fill="currentColor" />
        {[16, 21, 26].map((y, i) => (
          <rect
            key={y}
            x="6"
            y={y}
            width={i === 2 ? 20 : 32}
            height="2.5"
            rx="1"
            fill="currentColor"
            fillOpacity="0.35"
          />
        ))}
      </svg>
    </div>
  );
}

export const textDefinition = defineComponentType<TextComponent>({
  type: 'text',
  label: 'Text',
  description:
    "Markdown block (headings, lists, links, emphasis, tables) for commentary, headings and notes; `markdown` holds the text. Reads no dataset unless it binds one (`datasetKey` or `source`); then {column} placeholders in it show the dataset's totals, and {rows} / {total} its row counts.",
  version: 1,
  size: { w: 6, h: 3, minW: 2, minH: 1 },
  make: (id) => TextComponentSchema.parse({ type: 'text', id }),
  Renderer: TextRenderer,
  ConfigForm: TextConfigForm,
  Preview: TextPalettePreview,
});

/** Components that read no dataset. */
export const textWidgetRegistry: ComponentRegistry = {
  [textDefinition.type]: textDefinition,
};
